/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * PRE-ORDER / REGULAR CART SEPARATION
 *
 * The Stoq app only blocks a mixed cart in one direction (adding a pre-order while regular
 * items are in the cart), but lets regular items slip into a cart that already holds
 * pre-orders. This guard intercepts every add-to-cart request and enforces the rule in both
 * directions. To change the message, edit MIXED_CART_ERROR below.
 */
(function () {
  var MIXED_CART_TITLE = "Pre-orders must be ordered separately";
  var MIXED_CART_BODY =
    "Because release dates vary by title, pre-order items can't be combined with anything else in the same cart. Please order your pre-order on its own, then place a separate order for your other items";
  var MIXED_CART_ERROR = MIXED_CART_TITLE + ": " + MIXED_CART_BODY;

  function showMixedCartModal() {
    var existing = document.getElementById("nis-preorder-modal-overlay");
    if (existing) {
      return;
    }

    if (!document.getElementById("nis-preorder-modal-styles")) {
      var styles = document.createElement("style");
      styles.id = "nis-preorder-modal-styles";
      styles.textContent =
        ".nis-preorder-overlay{position:fixed;inset:0;background:rgba(15,15,15,.55);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;opacity:0;transition:opacity .2s ease}" +
        ".nis-preorder-overlay.is-visible{opacity:1}" +
        ".nis-preorder-modal{background:#fff;color:#1a1a1a;max-width:420px;width:100%;border-radius:10px;padding:32px 28px 24px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.3);transform:translateY(10px);transition:transform .2s ease;font-family:inherit}" +
        ".nis-preorder-overlay.is-visible .nis-preorder-modal{transform:translateY(0)}" +
        ".nis-preorder-modal svg{display:block;margin:0 auto 14px}" +
        ".nis-preorder-modal h2{margin:0 0 12px;font-size:19px;line-height:1.3;font-weight:700}" +
        ".nis-preorder-modal p{margin:0 0 22px;font-size:14px;line-height:1.65;color:#4a4a4a}" +
        ".nis-preorder-modal button{display:inline-block;background:#1a1a1a;color:#fff;border:none;border-radius:6px;padding:12px 40px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit}" +
        ".nis-preorder-modal button:hover{opacity:.85}";
      document.head.appendChild(styles);
    }

    var overlay = document.createElement("div");
    overlay.id = "nis-preorder-modal-overlay";
    overlay.className = "nis-preorder-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.innerHTML =
      '<div class="nis-preorder-modal">' +
      '<svg focusable="false" width="52" height="52" viewBox="0 0 52 52" aria-hidden="true">' +
      '<circle cx="26" cy="26" r="26" fill="#e02b27"></circle>' +
      '<path d="M18 18L34 34M34 18L18 34" stroke="#fff" stroke-width="4" stroke-linecap="round" fill="none"></path>' +
      "</svg>" +
      "<h2>" +
      MIXED_CART_TITLE +
      "</h2>" +
      "<p>" +
      MIXED_CART_BODY +
      "</p>" +
      '<button type="button">OK</button>' +
      "</div>";

    function close() {
      overlay.classList.remove("is-visible");
      setTimeout(function () {
        overlay.remove();
      }, 200);
      document.removeEventListener("keydown", onKeydown);
    }

    function onKeydown(event) {
      if (event.key === "Escape") {
        close();
      }
    }

    overlay.querySelector("button").addEventListener("click", close);
    overlay.addEventListener("click", function (event) {
      if (event.target === overlay) {
        close();
      }
    });
    document.addEventListener("keydown", onKeydown);

    document.body.appendChild(overlay);
    requestAnimationFrame(function () {
      overlay.classList.add("is-visible");
    });
    overlay.querySelector("button").focus();
  }

  document.addEventListener(
    "cart-notification:show",
    function (event) {
      if (event.detail && event.detail.error === MIXED_CART_ERROR) {
        event.stopImmediatePropagation();
        event.preventDefault();
        showMixedCartModal();
      }
    },
    true
  );

  var nativeFetch = window.fetch;

  function isPreorderCartItem(item) {
    if (item && item.selling_plan_allocation) {
      return true;
    }
    var properties = (item && item.properties) || {};
    return Object.keys(properties).some(function (key) {
      return (
        key.trim().toLowerCase() === "_is_preorder" &&
        properties[key] !== "" &&
        properties[key] !== false
      );
    });
  }

  function itemToResult(value) {
    if (!value || value.id == null || isNaN(parseInt(value.id, 10))) {
      return null;
    }
    var properties = value.properties || {};
    return {
      id: parseInt(value.id, 10),
      sellingPlan:
        value.selling_plan != null && value.selling_plan !== ""
          ? parseInt(value.selling_plan, 10)
          : null,
      isPreorder: Boolean(properties["_is_preorder"]),
    };
  }

  function extractAddedItems(body) {
    var entries = [];
    if (body instanceof FormData || body instanceof URLSearchParams) {
      entries = Array.from(body.entries());
    } else if (typeof body === "string") {
      try {
        entries = Object.entries(JSON.parse(body));
      } catch (error) {
        entries = Array.from(new URLSearchParams(body).entries());
      }
    } else if (body && typeof body === "object") {
      entries = Object.entries(body);
    }

    var rawItems = [];
    var single = null;
    entries.forEach(function (entry) {
      var key = entry[0];
      var value = entry[1];
      var preorderMatch =
        key.match(/^items\[(\d+)\]\[properties\]\[_is_preorder\]$/) ||
        key.match(/^items\[(\d+)\]\[properties\[_is_preorder\]\]$/);
      var match = key.match(/^items\[(\d+)\]\[([^\]]+)\]$/);
      if (preorderMatch) {
        var preorderIndex = parseInt(preorderMatch[1], 10);
        rawItems[preorderIndex] = rawItems[preorderIndex] || {};
        rawItems[preorderIndex]["properties"] = { _is_preorder: value };
      } else if (match) {
        var index = parseInt(match[1], 10);
        rawItems[index] = rawItems[index] || {};
        rawItems[index][match[2]] = value;
      } else if (key === "items" && Array.isArray(value)) {
        value.forEach(function (item) {
          if (item && item.id != null) {
            rawItems.push(item);
          }
        });
      } else if (key === "id") {
        single = single || {};
        single["id"] = value;
      } else if (key === "selling_plan") {
        single = single || {};
        single["selling_plan"] = value;
      } else if (key === "properties" && value && typeof value === "object") {
        single = single || {};
        single["properties"] = value;
      }
    });
    if (single) {
      rawItems.push(single);
    }
    return rawItems.map(itemToResult).filter(Boolean);
  }

  function isAddingPreorder(items) {
    return items.some(function (item) {
      return (
        (item.sellingPlan != null && !isNaN(item.sellingPlan)) ||
        item.isPreorder === true
      );
    });
  }

  function fetchCartItems() {
    return nativeFetch(window.themeVariables.routes.cartUrl + ".js", {
      credentials: "same-origin",
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return cart.items || [];
      })
      .catch(function () {
        return [];
      });
  }

  window.fetch = async function (input, init) {
    try {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var method = ((init && init.method) || "GET").toUpperCase();
      var body = init && init.body;
      if (method === "POST" && url.indexOf("/cart/add") !== -1 && body) {
        var addedItems = extractAddedItems(body);
        if (addedItems.length > 0) {
          var addingPreorder = isAddingPreorder(addedItems);
          var cartItems = await fetchCartItems();
          var cartHasPreorder = cartItems.some(isPreorderCartItem);
          if (cartItems.length > 0 && cartHasPreorder !== addingPreorder) {
            return new Response(
              JSON.stringify({
                status: 422,
                message: "Cart Error",
                description: MIXED_CART_ERROR,
              }),
              { status: 422, headers: { "Content-Type": "application/json" } }
            );
          }
        }
      }
    } catch (error) {
      console.warn("Pre-order cart guard skipped:", error);
    }
    return nativeFetch.apply(window, arguments);
  };
})();

/**
 * ADD-TO-CART WATCHDOG
 *
 * When the Stoq mixed-cart popup is closed, it cancels the add-to-cart request without
 * ever settling it, so the theme never re-enables the submit button and it spins forever.
 * This watchdog re-enables the buttons if a submit is still stuck after 2 seconds.
 */
(function () {
  var WATCHDOG_DELAY = 2000;

  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (!form || typeof form.matches !== "function") {
        return;
      }
      if (!form.matches('form[is="product-form"], form[action*="/cart/add"]')) {
        return;
      }
      setTimeout(function () {
        if (!form.isConnected) {
          return;
        }
        form.querySelectorAll('[type="submit"]').forEach(function (button) {
          if (
            button.hasAttribute("disabled") &&
            button.getAttribute("aria-busy") === "true"
          ) {
            button.removeAttribute("disabled");
            button.removeAttribute("aria-busy");
          }
        });
      }, WATCHDOG_DELAY);
    },
    true
  );
})();
